import { createContext, ReactNode, useCallback, useContext, useState, useMemo } from 'react';
import { useParams } from 'react-router';

import { useNavigation } from 'hooks/useNavigation';
import { UsersService } from 'services';
import { GetCurrentUserPermissionsResponse, GetCurrentUserResponse } from 'services/users';
import { StringifiedUUID, CollaboratorRoleAccessType } from 'types';
import { ChangeHistoryAnchorObject } from 'types/changeHistory';
import { DataField } from 'types/dataFields';
import { DataFieldValue } from 'types/dataFieldValue';
import { PlanningCycle } from 'types/planningCycles';
import { Events } from 'utils/events';

// TODO: (TQ-956) This is getting a little out of hand with so many props. This can probably be broken up
// in to multiple sub-hooks divided into product areas.
export type TQModelContextType = {
  tqModelId: StringifiedUUID;
  isMoveChildNodeModalOpen: boolean;
  isDeleteTerritoryModalOpen: boolean;
  isEditNodeModalOpen: boolean;
  isAddTerritoryParentOpen: boolean;
  isAddTerritoryChildOpen: boolean;
  isAccountHistoryOpen: boolean;
  lastRefreshTimestamp: number | undefined;
  setIsDeleteTerritoryModalOpen: Function;
  setIsMoveChildNodeModalOpen: Function;
  setIsEditNodeModalOpen: Function;
  setAddTerritoryParentOpen: Function;
  setAddTerritoryChildOpen: Function;
  user?: GetCurrentUserResponse;
  userPermissions?: GetCurrentUserPermissionsResponse;
  setTerritoryGridData: Function;
  territoryGridData: DataFieldValue[];
  setAccountsGridData: Function;
  accountsGridData: DataFieldValue[];
  setTerritoryDataFields: Function;
  territoryDataFields: DataField[];
  totalTerritories: number | undefined;
  setTotalTerritories: Function;
  totalAccounts: number | undefined;
  setTotalAccounts: Function;
  accessMode?: CollaboratorRoleAccessType;
  setLastRefreshTimestamp: Function;
  planningCycle: PlanningCycle | null;
  setPlanningCycle: Function;
  globalTerritoriesCount: number;
  setGlobalTerritoriesCount: Function;
  changeHistoryAnchorObject: ChangeHistoryAnchorObject | undefined;
  openChangeHistory: (anchorObject: ChangeHistoryAnchorObject) => void;
  closeChangeHistory: () => void;
  isTreeMaximized: boolean;
  setIsTreeMaximized: Function;
};

const TQModelContext = createContext<TQModelContextType>({} as TQModelContextType);
export const useTQModelContext = () => useContext(TQModelContext);

type TQModelContextProps = {
  children: ReactNode;
  territoryDataFields?: DataField[];
  territoryGridData?: DataFieldValue[];
  totalAccounts?: number;
};

export const TQModelContextProvider = ({
  children,
  territoryDataFields: initialTerritoryDataFields = [],
  territoryGridData: initialTerritoryGridData = [],
  totalAccounts: initialTotalAccounts = 0,
}: TQModelContextProps) => {
  const [isMoveChildNodeModalOpen, setIsMoveChildNodeModalOpen] = useState<boolean>(false);
  const [isDeleteTerritoryModalOpen, setIsDeleteTerritoryModalOpen] = useState<boolean>(false);
  const [isEditNodeModalOpen, setIsEditNodeModalOpen] = useState<boolean>(false);
  const [isAddTerritoryParentOpen, setAddTerritoryParentOpen] = useState<boolean>(false);
  const [isAddTerritoryChildOpen, setAddTerritoryChildOpen] = useState<boolean>(false);
  const user = UsersService.useGetCurrentUserQuery().query.data;
  const userPermissions = UsersService.useGetCurrentUserPermissions().query.data;
  const [territoryGridData, setTerritoryGridData] = useState<DataFieldValue[]>(initialTerritoryGridData);
  const [accountsGridData, setAccountsGridData] = useState<DataFieldValue[]>([]);
  const [territoryDataFields, setTerritoryDataFields] = useState<DataField[]>(initialTerritoryDataFields);
  const [totalTerritories, setTotalTerritories] = useState<number | undefined>(initialTerritoryGridData?.length || 0);
  const [totalAccounts, setTotalAccounts] = useState<number | undefined>(initialTotalAccounts);
  const [planningCycle, setPlanningCycle] = useState<PlanningCycle | null>(null);
  const [globalTerritoriesCount, setGlobalTerritoriesCount] = useState<number>(0);
  const [isAccountHistoryOpen, setIsAccountHistoryOpen] = useState<boolean>(false);
  const [changeHistoryAnchorObject, setChangeHistoryAnchorObject] = useState<ChangeHistoryAnchorObject>();

  const [isTreeMaximized, setIsTreeMaximized] = useState<boolean>(false);

  // TODO: update this logic when proposed changes is integrated
  const [lastRefreshTimestamp, setLastRefreshTimestamp] = useState<number | undefined>();
  const { tqModelId: tqModelIdParam } = useParams<{ tqModelId: string }>();
  const { extractSpmModelIdFromPath } = useNavigation();
  // We need to extract the spmModelId from the current path without using useParams.
  // This is because this context is attached at a higher level in our app (i.e. PlanDetailView) where the
  // routed path does not include the spmModelId. So instead we extract it from the current path.
  // Make this reactive to URL changes by using useMemo
  const tqModelId = useMemo(() => {
    const spmModelId = extractSpmModelIdFromPath();
    return spmModelId || tqModelIdParam;
  }, [extractSpmModelIdFromPath, tqModelIdParam]);

  const openChangeHistory = useCallback((anchorObject: ChangeHistoryAnchorObject) => {
    Events.track(Events.EVENT.account_history.summary.displayed, {
      data_object_id: anchorObject.id,
      data_object_name: anchorObject.name,
    });

    setChangeHistoryAnchorObject(anchorObject);
    setIsAccountHistoryOpen(true);
  }, []);

  const closeChangeHistory = useCallback(() => {
    Events.track(Events.EVENT.account_history.summary.hidden, {
      data_object_id: changeHistoryAnchorObject?.id,
      data_object_name: changeHistoryAnchorObject?.name,
    });

    setChangeHistoryAnchorObject(undefined);
    setIsAccountHistoryOpen(false);
  }, [changeHistoryAnchorObject]);

  return (
    <TQModelContext.Provider
      value={{
        tqModelId,
        isMoveChildNodeModalOpen,
        isDeleteTerritoryModalOpen,
        isEditNodeModalOpen,
        isAddTerritoryParentOpen,
        isAddTerritoryChildOpen,
        lastRefreshTimestamp,
        isAccountHistoryOpen,
        setIsDeleteTerritoryModalOpen,
        setIsMoveChildNodeModalOpen,
        setIsEditNodeModalOpen,
        setAddTerritoryParentOpen,
        setAddTerritoryChildOpen,
        user,
        userPermissions,
        setTerritoryGridData,
        territoryGridData,
        setTerritoryDataFields,
        territoryDataFields,
        totalTerritories,
        setTotalTerritories,
        totalAccounts,
        setTotalAccounts,
        setLastRefreshTimestamp,
        planningCycle,
        setPlanningCycle,
        setAccountsGridData,
        accountsGridData,
        globalTerritoriesCount,
        setGlobalTerritoriesCount,
        changeHistoryAnchorObject,
        openChangeHistory,
        closeChangeHistory,
        isTreeMaximized,
        setIsTreeMaximized,
      }}
    >
      {children}
    </TQModelContext.Provider>
  );
};
